import React from 'react';
import { CONNECTION_STATUS, LINK_LIMITS } from '../lib/rcu/catalog/link';
import { useRcuConfigStore } from '../stores/rcuConfigStore';
import { useRcuLinkStore } from '../stores/rcuLinkStore';

/**
 * Keeps the broker link up for the whole app, not just for the console.
 *
 * The room controls show the RCU's own state, and that state arrives when the
 * device says so — so the session has to be alive while a guest is using the
 * tablet, not only while a technician is inside the hidden console. This host
 * owns that: it connects whenever a broker is configured and a serial number is
 * set, reconnects when either changes, and closes the session when the broker is
 * cleared. Locking the console no longer touches it.
 *
 * The one snapshot it takes is on becoming live, and again when the rails
 * change while live — a rail added a minute ago has channels nobody has read.
 * Nothing here polls: from then on the RCU reports its own changes.
 *
 * A tablet with a broker configured therefore holds a session for as long as the
 * app runs. That is the price of guest-visible state; the transport preference
 * decides separately whether commands use it.
 */
export const LinkHost: React.FC = () => {
  const connect = useRcuLinkStore((state) => state.connect);
  const disconnect = useRcuLinkStore((state) => state.disconnect);
  const hydrate = useRcuLinkStore((state) => state.hydrate);
  const status = useRcuLinkStore((state) => state.link.status);
  const { bridgeUrl, bridgeUsername, bridgePassword } = useRcuLinkStore((state) => state.settings);
  const serial = useRcuConfigStore((state) => state.config.rcu.serialNumber.trim());
  const rails = useRcuConfigStore((state) => state.config.rails);
  /**
   * Connect on the gateway's credentials, not on the configuration object:
   * renaming a load must not drop a working session, and neither must changing
   * the transport preference, which only decides how commands leave. The gateway
   * is the only MQTT route there is, so its address is the only thing here that
   * can change where the session lands. The settings form saves as the
   * technician types, so a change is given a moment to settle before the socket
   * is rebuilt, and the config is read at call time so the session always sees
   * the current installation.
   *
   * An empty address is not a reason to stay closed: it means the gateway is
   * wherever this app came from, which is the usual arrangement.
   */
  React.useEffect(() => {
    if (serial === '') {
      disconnect();
      return;
    }

    const timer = setTimeout(() => {
      void connect(useRcuConfigStore.getState().config);
    }, LINK_LIMITS.SETTINGS_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [bridgePassword, bridgeUrl, bridgeUsername, connect, disconnect, serial]);

  /**
   * One snapshot per becoming-live, and one per change of rails while live: the
   * rails are the part of the configuration that decides what to read, so they,
   * not the whole object, are what this effect watches. The config itself is read
   * at call time for the same reason as above.
   */
  React.useEffect(() => {
    if (status !== CONNECTION_STATUS.ONLINE) return;
    hydrate(useRcuConfigStore.getState().config);
  }, [hydrate, rails, status]);

  return null;
};

export default LinkHost;
